import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { NotificationHelper } from 'src/app/core/services/helpers/notification.helper';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { AgrupacionNovedadesService, AgrupacionNovedad } from '../../services/agrupacion-novedades.service';
import { TipoNovedadService, TipoNovedadOption } from '../../services/tipo-novedad.service';
import { finalize } from 'rxjs/operators';
import { showSpinner, hideSpinner, getCurrentUser } from 'src/app/core/services/functions.service';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

interface AgrupacionGrupo {
  codigoagrupacion: string;
  novedades: AgrupacionNovedad[];
  estado: string;
  c_emp: string;
}

@Component({
  selector: 'app-agrupacionnovedades',
  templateUrl: './agrupacionnovedades.component.html',
  styleUrls: ['./agrupacionnovedades.component.scss']
})
export class AgrupacionnovedadesComponent implements OnInit {
  agrupacionForm: FormGroup;
  searchControl = new FormControl('');
  gruposAgrupaciones: AgrupacionGrupo[] = [];
  gruposAgrupacionesOriginal: AgrupacionGrupo[] = [];
  displayedColumns: string[] = ['codigonovedad', 'estado'];
  isEditing = false;
  isLoading = false;
  editingId: number | null = null;
  tiposNovedad: TipoNovedadOption[] = [];
  tiposNovedadSeleccionados: TipoNovedadOption[] = [];
  tipoNovedadCtrl = new FormControl('');
  tiposNovedadFiltrados: Observable<TipoNovedadOption[]>;
  expandedGroup: string | null = null;
  novedadesOriginales: AgrupacionNovedad[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('autoTipoNovedad') matAutocomplete!: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private notificationHelper: NotificationHelper,
    private agrupacionService: AgrupacionNovedadesService,
    private tipoNovedadService: TipoNovedadService
  ) {
    this.agrupacionForm = this.fb.group({
      codigoagrupacion: ['', Validators.required],
      codigonovedad: [[]]
    });

    this.tiposNovedadFiltrados = this.tipoNovedadCtrl.valueChanges.pipe(
      startWith(null),
      map(value => this._filtrarTiposNovedad(value))
    );
  }

  ngOnInit(): void {
    this.cargarDatos();
    this.cargarTiposNovedad();
    this.searchControl.valueChanges.subscribe(value => {
      this.filtrarGrupos(value);
    });
  }

  ngAfterViewInit() {
    if (this.autocompleteTrigger) {
      // Prevenir que el panel se cierre al hacer click
      this.autocompleteTrigger.autocomplete._keyManager?.withHorizontalOrientation(null);
    }
  }

  private _filtrarTiposNovedad(value: string | TipoNovedadOption | null): TipoNovedadOption[] {
    if (!value) return this.tiposNovedad;
    
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    return this.tiposNovedad.filter(tipo => 
      tipo.codigo.toLowerCase().includes(filterValue) || 
      tipo.descripcion.toLowerCase().includes(filterValue)
    );
  }

  mostrarListaTiposNovedad(): void {
    this.tipoNovedadCtrl.setValue('');
    setTimeout(() => {
      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.openPanel();
      }
    });
  }

  getTipoNovedadPlaceholder(): string {
    if (this.tiposNovedadSeleccionados.length === 0) {
      return 'Seleccione tipos de novedad';
    }
    return this.tiposNovedadSeleccionados
      .map(t => t.codigo)
      .join(', ');
  }

  displayTipoNovedad(option: TipoNovedadOption): string {
    return option ? option.descripcion : '';
  }

  isTipoNovedadSeleccionado(tipo: TipoNovedadOption): boolean {
    return this.tiposNovedadSeleccionados.some(t => t.codigo === tipo.codigo);
  }

  private validarTipoNovedadDuplicado(codigoAgrupacion: string, tipoNovedad: string): boolean {
    // Si estamos editando, no validamos contra el mismo grupo
    if (this.isEditing && codigoAgrupacion === this.agrupacionForm.get('codigoagrupacion')?.value) {
      return false;
    }

    // Buscar en el array de grupos existentes
    const grupo = this.gruposAgrupaciones.find(g => g.codigoagrupacion === codigoAgrupacion);
    
    if (grupo) {
      // Verificar si el tipo de novedad ya existe en ese grupo
      return grupo.novedades.some(n => n.codigonovedad === tipoNovedad);
    }
    
    return false;
  }

  toggleSeleccionTipoNovedad(tipo: TipoNovedadOption, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    
    const codigoAgrupacion = this.agrupacionForm.get('codigoagrupacion')?.value;
    const index = this.tiposNovedadSeleccionados.findIndex(t => t.codigo === tipo.codigo);
    
    // Si está intentando agregar uno nuevo
    if (index === -1) {
      // Validar si ya existe en otra agrupación
      if (this.validarTipoNovedadDuplicado(codigoAgrupacion, tipo.codigo)) {
        this.notificationHelper.errorMessage(
          'El tipo de novedad "' + tipo.descripcion + '" ya existe en esta agrupación'
        );
        return;
      }
      // Si no está duplicado, agregarlo
      this.tiposNovedadSeleccionados.push(tipo);
    } else {
      // Si ya existe, removerlo
      this.tiposNovedadSeleccionados.splice(index, 1);
    }
    
    // Limpiar el control después de la selección
    this.tipoNovedadCtrl.setValue('');
  }

  editarAgrupacionCompleta(grupo: AgrupacionGrupo) {
    // Buscar el ID de la primera novedad activa del grupo
    const primeraNovedadActiva = grupo.novedades.find(n => n.estado === 'A');
    if (!primeraNovedadActiva || !primeraNovedadActiva.id) {
      this.notificationHelper.errorMessage('No se encontró una novedad activa para editar');
      return;
    }

    this.editingId = primeraNovedadActiva.id;
    
    // Guardar las novedades originales
    this.novedadesOriginales = [...grupo.novedades];
    
    // Cargar los tipos de novedad seleccionados
    const tiposSeleccionados = grupo.novedades
      .filter(n => n.estado === 'A')
      .map(n => {
        const tipo = this.tiposNovedad.find(t => t.codigo === n.codigonovedad);
        if (!tipo) {
          this.notificationHelper.errorMessage(
            'No se encontró el tipo de novedad con código: ' + n.codigonovedad
          );
          return null;
        }
        return tipo;
      }).filter((t): t is TipoNovedadOption => t !== null);

    // Actualizar el formulario
    this.agrupacionForm.patchValue({
      codigoagrupacion: grupo.codigoagrupacion
    });

    // Actualizar la selección de tipos de novedad
    this.tiposNovedadSeleccionados = tiposSeleccionados;
    this.tipoNovedadCtrl.setValue('');

    this.isEditing = true;
  }

  cancelarEdicion() {
    this.isEditing = false;
    this.novedadesOriginales = [];
    this.agrupacionForm.reset();
    this.tiposNovedadSeleccionados = [];
    this.tipoNovedadCtrl.setValue('');
  }

  cargarDatos(): void {
    showSpinner();
    this.agrupacionService.getAllAgrupaciones('JA')
      .pipe(finalize(() => hideSpinner()))
      .subscribe({
        next: (data) => {
          this.gruposAgrupaciones = this.agruparDatos(data);
          this.gruposAgrupacionesOriginal = [...this.gruposAgrupaciones];
          this.notificationHelper.successMessage('Datos cargados correctamente');
        },
        error: (error) => {
          this.notificationHelper.errorMessage('Error al cargar las agrupaciones: ' + error.message);
        }
      });
  }

  toggleGrupo(codigoagrupacion: string) {
    this.expandedGroup = this.expandedGroup === codigoagrupacion ? null : codigoagrupacion;
  }

  onSubmit(): void {
    if (this.agrupacionForm.valid) {
      const currentUser = getCurrentUser();
      
      if (!currentUser || !currentUser.c_emp || !currentUser.usuario_aplicativo) {
        this.notificationHelper.errorMessage('No se pudo obtener la información completa del usuario actual');
        return;
      }

      // Type assertion para asegurar que TypeScript reconozca los tipos
      const userEmp = currentUser.c_emp as string;
      const userApp = currentUser.usuario_aplicativo as string;

      const formData = this.agrupacionForm.value;
      showSpinner();
      
      if (this.isEditing) {
        const codigoAgrupacion = formData.codigoagrupacion;

        // Si no hay novedades seleccionadas, eliminar todos los registros
        if (this.tiposNovedadSeleccionados.length === 0) {
          const deletePromises = this.novedadesOriginales
            .filter(nov => nov.estado === 'A')
            .map(nov => this.agrupacionService.deleteAgrupacion(nov.id!, 'JA').toPromise());

          Promise.all(deletePromises)
            .then(() => {
              this.notificationHelper.successMessage('Registros eliminados correctamente');
              this.cancelarEdicion();
              this.cargarDatos();
            })
            .catch(error => {
              this.notificationHelper.errorMessage('Error al eliminar los registros: ' + error.message);
            })
            .finally(() => {
              hideSpinner();
            });
          return;
        }

        // 1. Identificar novedades a eliminar (las que estaban pero ya no están seleccionadas)
        const novedadesAEliminar = this.novedadesOriginales
          .filter(nov => nov.estado === 'A' && 
                        !this.tiposNovedadSeleccionados.some(t => t.codigo === nov.codigonovedad));

        // 2. Identificar novedades nuevas a crear
        const novedadesACrear = this.tiposNovedadSeleccionados
          .filter(tipo => !this.novedadesOriginales.some(nov => 
            nov.codigonovedad === tipo.codigo && nov.estado === 'A'
          ))
          .map(tipo => ({
            codigoagrupacion: codigoAgrupacion,
            codigonovedad: tipo.codigo,
            c_emp: userEmp,
            estado: 'A',
            usuariocreacion: userApp
          }));

        // 3. Crear array de promesas para todas las operaciones
        const deletePromises = novedadesAEliminar
          .map(nov => this.agrupacionService.deleteAgrupacion(nov.id!,'JA').toPromise());
        
        const createPromises = novedadesACrear
          .map(nov => this.agrupacionService.createAgrupacion(nov,'JA').toPromise());

        // 4. Ejecutar todas las operaciones
        Promise.all([...deletePromises, ...createPromises])
          .then(() => {
            this.notificationHelper.successMessage('Agrupación actualizada correctamente');
            this.cancelarEdicion();
            this.cargarDatos();
          })
          .catch(error => {
            this.notificationHelper.errorMessage('Error al actualizar la agrupación: ' + error.message);
          })
          .finally(() => {
            hideSpinner();
          });
      } else {
        // Crear nuevas agrupaciones
        if (this.tiposNovedadSeleccionados.length === 0) {
          this.notificationHelper.errorMessage('Por favor, seleccione al menos un tipo de novedad');
          hideSpinner();
          return;
        }

        const promises = this.tiposNovedadSeleccionados.map(tipo => {
          return this.agrupacionService.createAgrupacion({
            codigoagrupacion: formData.codigoagrupacion,
            codigonovedad: tipo.codigo,
            c_emp: userEmp,
            estado: 'A',
            usuariocreacion: userApp
          }, 'JA').toPromise();
        });

        Promise.all(promises)
          .then(() => {
            this.notificationHelper.successMessage('Agrupaciones creadas exitosamente');
            this.cargarDatos();
            this.agrupacionForm.reset();
            this.tiposNovedadSeleccionados = [];
          })
          .catch(error => {
            this.notificationHelper.errorMessage('Error al crear las agrupaciones: ' + error);
          })
          .finally(() => {
            hideSpinner();
          });
      }
    }
  }

  eliminarAgrupacion(element: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { message: '¿Está seguro de eliminar esta agrupación?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && element.id) {
        showSpinner();
        this.agrupacionService.deleteAgrupacion(element.id, 'JA')
          .pipe(finalize(() => hideSpinner()))
          .subscribe({
            next: () => {
              this.notificationHelper.successMessage('Agrupación eliminada correctamente');
              this.cargarDatos();
            },
            error: (error) => {
              this.notificationHelper.errorMessage('Error al eliminar: ' + error.message);
            }
          });
      }
    });
  }

  resetForm(): void {
    this.isEditing = false;
    this.editingId = null;
    this.novedadesOriginales = [];
    this.tiposNovedadSeleccionados = [];
    this.agrupacionForm.reset();
  }

  getTipoNovedadDescripcion(codigo: string): string {
    return this.tiposNovedad.find(t => t.codigo === codigo)?.descripcion || '';
  }

  getDescripcionNovedad(codigo: string): string {
    const tipo = this.tiposNovedad.find(t => t.codigo === codigo);
    return tipo ? tipo.descripcion : codigo;
  }

  filtrarGrupos(value: string) {
    const filterValue = value?.toLowerCase() || '';
    if (!filterValue) {
      this.gruposAgrupaciones = [...this.gruposAgrupacionesOriginal];
      return;
    }
    
    this.gruposAgrupaciones = this.gruposAgrupacionesOriginal.filter(grupo => {
      return grupo.codigoagrupacion.toLowerCase().includes(filterValue) ||
             grupo.novedades.some(nov => 
               nov.codigonovedad.toLowerCase().includes(filterValue) ||
               (this.getTipoNovedadDescripcion(nov.codigonovedad) || '').toLowerCase().includes(filterValue)
             );
    });
  }

  displayFn(option: TipoNovedadOption): string {
    return option ? `${option.codigo} - ${option.descripcion}` : '';
  }

  removeNovedadSeleccionada(novedad: TipoNovedadOption) {
    const index = this.tiposNovedadSeleccionados.indexOf(novedad);
    if (index >= 0) {
      this.tiposNovedadSeleccionados.splice(index, 1);
    }
  }

  cargarTiposNovedad(): void {
    showSpinner();
    this.tipoNovedadService.getTiposNovedadActivos('JA')
      .pipe(finalize(() => hideSpinner()))
      .subscribe({
        next: (tipos) => {
          this.tiposNovedad = tipos;
        },
        error: (error) => {
          this.notificationHelper.errorMessage('Error al cargar tipos de novedad: ' + error.message);
        }
      });
  }

  agruparDatos(data: AgrupacionNovedad[]): AgrupacionGrupo[] {
    const grupos = new Map<string, { novedades: AgrupacionNovedad[], estado: string, c_emp: string }>();
    
    data.forEach(item => {
      if (!grupos.has(item.codigoagrupacion)) {
        grupos.set(item.codigoagrupacion, { novedades: [], estado: 'I', c_emp: item.c_emp });
      }
      if (item.estado === 'A') {
        grupos.get(item.codigoagrupacion)?.novedades.push(item);
        grupos.get(item.codigoagrupacion)!.estado = 'A';
      }
    });

    return Array.from(grupos.entries())
      .map(([codigo, grupo]) => ({
        codigoagrupacion: codigo,
        novedades: grupo.novedades,
        estado: grupo.estado,
        c_emp: grupo.c_emp
      }));
  }

  onCodigoAgrupacionInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    
    // Convertir todo el texto a mayúsculas
    value = value.toUpperCase();
    
    // Actualizar el valor en el formulario
    this.agrupacionForm.get('codigoagrupacion')?.setValue(value, { emitEvent: false });
  }
}
